import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { X, Play, Square, Send, Clock, Workflow } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auto-scheduler + 31-day drip preview + today's pending WhatsApp messages.
// Opens as a clean side/modal panel from the CRM header.
export default function AutomationPanel({ token, onClose }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState([]);
  const [feature, setFeature] = useState(null);
  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/scheduler/status`);
      const d = await res.json();
      if (d.success) setStatus(d);
    } catch (e) { console.error('scheduler', e); }
  }, []);

  const fetchPending = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/lead-nurturing/pending-messages`);
      const d = await res.json();
      if (d.success) setPending(d.messages || []);
    } catch (e) { console.error('pending', e); }
  }, []);

  const previewFeature = useCallback(async (day) => {
    try {
      const res = await fetch(`${API_URL}/api/lead-nurturing/feature/${day}?language=hi`);
      const d = await res.json();
      if (d.success) setFeature(d);
    } catch (e) { console.error('feature', e); }
  }, []);

  useEffect(() => { fetchStatus(); fetchPending(); previewFeature(1); }, [fetchStatus, fetchPending, previewFeature]);

  const running = status?.scheduler?.running;

  const toggle = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/scheduler/${running ? 'stop' : 'start'}`, { method: 'POST' });
      const d = await res.json();
      if (d.success) fetchStatus();
    } catch (e) { console.error(e); } finally { setBusy(false); }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/scheduler/run-now`, { method: 'POST' });
      const d = await res.json();
      if (d.success) { toast.success(`${d.messages_sent || 0} messages भेजे गए`); fetchStatus(); fetchPending(); }
    } catch (e) { console.error(e); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/20 backdrop-blur-sm" onClick={onClose} data-testid="crm-automation-panel">
      <div className="bg-white border-l border-zinc-200 w-full max-w-md h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-zinc-200 flex items-start justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Workflow className="w-5 h-5 text-zinc-900" strokeWidth={2} />
            <div>
              <h3 className="text-lg font-semibold tracking-tight text-zinc-900">Automation</h3>
              <p className="text-xs text-zinc-500">Auto-scheduler व 31-दिन drip campaign</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="crm-automation-close" className="text-zinc-400 hover:text-zinc-900"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-6">
          {/* Scheduler */}
          <div className="border border-zinc-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-zinc-500" />
                <span className="text-sm font-medium text-zinc-900">Auto Scheduler</span>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${running ? 'border-green-200 bg-green-50 text-green-700' : 'border-zinc-200 bg-zinc-50 text-zinc-500'}`}>
                  {running ? 'चालू' : 'बंद'}
                </span>
              </div>
            </div>
            <p className="text-xs text-zinc-500 mt-2 tabular-nums">
              {status?.campaigns?.active || 0} active campaigns · {status?.scheduler?.messages_sent_today || 0} messages आज
            </p>
            <div className="flex gap-2 mt-3">
              <button onClick={toggle} disabled={busy} data-testid="crm-scheduler-toggle"
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium disabled:opacity-50 ${running ? 'border border-zinc-200 text-zinc-700 hover:bg-zinc-50' : 'bg-zinc-900 text-white hover:bg-zinc-800'}`}>
                {running ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />} {running ? 'बंद करें' : 'शुरू करें'}
              </button>
              <button onClick={runNow} disabled={busy} data-testid="crm-scheduler-run"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium border border-zinc-200 text-zinc-700 hover:bg-zinc-50 disabled:opacity-50">
                <Send className="w-3.5 h-3.5" /> अभी भेजें
              </button>
            </div>
          </div>

          {/* Today's pending messages */}
          {pending.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">आज भेजने हैं ({pending.length})</p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {pending.map((m, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border border-zinc-100 rounded-md px-3 py-2">
                    <span className="text-zinc-600 tabular-nums">{m.lead_phone}</span>
                    <span className="text-zinc-800 text-xs">Day {m.day}: {m.feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 31-day drip feature preview */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">31-दिन Drip — Feature Preview</p>
            <div className="flex gap-1 flex-wrap mb-3">
              {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                <button key={day} onClick={() => previewFeature(day)} data-testid={`crm-drip-day-${day}`}
                  className={`w-7 h-7 rounded-md text-xs font-medium tabular-nums ${feature?.day === day ? 'bg-zinc-900 text-white' : 'border border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`}>
                  {day}
                </button>
              ))}
            </div>
            {feature && (
              <div className="border border-zinc-200 rounded-lg p-3">
                <p className="text-sm font-medium text-zinc-900 mb-2">Day {feature.day}: {feature.feature}</p>
                <p className="text-sm text-zinc-600 whitespace-pre-wrap leading-relaxed">{feature.message}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
